import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import type { ValueOf } from '@/helpers/types';
import type { ComputedRef, Ref } from 'vue';

import { MonetizationType } from '@/@types/graphql-types';
import { WebLocale } from '@/enums/web-locale';
import { toValue } from 'vue';
import { computed, onMounted, onServerPrefetch } from 'vue';

import { useTitleDetailsStore } from '@/stores/pinia/useTitleDetailsStore';
import { storeToRefs } from 'pinia';
import { useExperiment } from '@/libraries/experiment/composables/useExperiment';

// TYPES
type digits = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
type day = `${'0'}${Exclude<digits, '0'>}` | `${'1' | '2'}${digits}` | '30' | '31';
type month = `${'0'}${Exclude<digits, '0'>}` | '10' | '11' | '12';
type year = `20${digits}${digits}`;
type ISO8601 = `${year}-${month}-${day}`;

/** Provider promotions are only for titles with Flatrate and free offers of the promo provider. */
const flatrateOrFreeOrAds = [MonetizationType.Flatrate, MonetizationType.Free, MonetizationType.Ads];

interface PromoText {
	/**
	 * Angled ribbon over square provider icons or
	 * drawer below wide provider icons.
	 */
	ribbon?: string;

	/**
	 * Ribbon text exclusively for wide provider icons
	 * which have room for a bit more text.
	 */
	wideRibbon?: string;

	/**
	 * Text below the square provider in GridOffer (next to 4k/quality).
	 * Text above the price in ListOffer.
	 */
	offerText?: string;

	/** Text that appears on the backdrop play button. */
	longText?: string;

	/** Optional promotion start date in `YYYY-MM-DD` format. */
	startDate?: ISO8601;

	/** `YYYY-MM-DD` format. */
	endDate: ISO8601;

	/** 3 character shortname of the promoted provider. */
	provider: string;

	/** The Url of the promoted provider which overrides the regular offer link. */
	url?: string;

	/** Disclaimer text - for additional Terms and Conditions, passed into the DisclaimerButton */
	disclaimerText?: string;

	/** Secondary disclaimer text for BuyBoxOfferNew - for additional Terms and Conditions, passed into the DisclaimerButton */
	offerDisclaimerText?: string;

	/** Suppress the provider promotion on the backdrop play button --> was a special requirement from HBO Max GB promotion to only have the buybox offer promotions, but assumption is that this could be more common */
	hideBackdropPromo?: boolean;

	experimentName?: string; // for testing purposes to link the promo to a specific experiment variant
}

/** Normal PromoText config with the Provider Icon URL */
export type PromoTextWithIcon = PromoText & { icon: string };

// Change uk to gb in the WebLocale list and return it upper case to match store country
type PromotionLocale = Uppercase<Exclude<keyof typeof WebLocale, 'uk'> | 'gb'>;

const promotions = {
	AR: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta limitada',
			startDate: '2026-05-28',
			endDate: '2026-06-17',
			provider: 'dnp',
			longText: 'Plan Premium de Disney+ con un 50% de descuento',
			disclaimerText: 'Más info en DisneyPlus.com',
			offerDisclaimerText: 'Más info en DisneyPlus.com',
		},
	],
	BR: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta limitada',
			startDate: '2026-05-28',
			endDate: '2026-06-17',
			provider: 'dnp',
			longText: 'Disney+ a partir de R$14,90/mês',
			disclaimerText: 'Aplicam-se termos e condições. Mais info em DisneyPlus.com',
			offerDisclaimerText: 'Aplicam-se termos e condições. Mais info em DisneyPlus.com',
		},
	],
	CL: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta limitada',
			startDate: '2026-05-28',
			endDate: '2026-06-17',
			provider: 'dnp',
			longText: 'Plan Premium de Disney+ con un 45% de descuento',
			disclaimerText: 'Más info en DisneyPlus.com',
			offerDisclaimerText: 'Más info en DisneyPlus.com',
		},
	],
	CO: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta limitada',
			startDate: '2026-05-28',
			endDate: '2026-06-17',
			provider: 'dnp',
			longText: 'Planes desde $14.900/mes. Oferta limitada.',
			disclaimerText: 'Más info en DisneyPlus.com',
			offerDisclaimerText: 'Más info en DisneyPlus.com',
		},
	],
	EC: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta limitada',
			startDate: '2026-05-28',
			endDate: '2026-06-17',
			provider: 'dnp',
			longText: 'Plan Premium de Disney+ con un 32% de descuento',
			disclaimerText: 'Más info en DisneyPlus.com',
			offerDisclaimerText: 'Más info en DisneyPlus.com',
		},
	],
	MX: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta limitada',
			startDate: '2026-05-28',
			endDate: '2026-06-17',
			provider: 'dnp',
			longText: 'Planes desde $79/mes. Oferta limitada.',
			disclaimerText: 'Más info en DisneyPlus.com',
			offerDisclaimerText: 'Más info en DisneyPlus.com',
		},
	],
} satisfies Partial<Record<PromotionLocale, PromoText[]>>;

type PromotionCountries = keyof typeof promotions;
type PromotionProviders = ValueOf<typeof promotions>[number]['provider'];

const promotionProviderIcons: Record<PromotionProviders, string> = {
	amp: `${JW_CONFIG.IMAGESCALER_URL}/icon/322992749/s100/amazonprime.webp`,
	prv: `${JW_CONFIG.IMAGESCALER_URL}/icon/322992749/s100/amazonprime.webp`,
	hst: `${JW_CONFIG.IMAGESCALER_URL}/icon/324950024/s100/hotstar.webp`,
	dnp: `${JW_CONFIG.IMAGESCALER_URL}/icon/313118777/s100/disneyplus.webp`,
	atp: `${JW_CONFIG.IMAGESCALER_URL}/icon/152862153/s100/appletvplus.webp`,
	bng: `${JW_CONFIG.IMAGESCALER_URL}/icon/183470172/s100/binge.webp`,
	pmp: `${JW_CONFIG.IMAGESCALER_URL}/icon/242706661/s100/paramountplus.webp`,
	sst: `${JW_CONFIG.IMAGESCALER_URL}/icon/300772362/s100/skyshowtime.webp`,
	mxx: `${JW_CONFIG.IMAGESCALER_URL}/icon/305458112/s100/max.webp`,
	tbv: `${JW_CONFIG.IMAGESCALER_URL}/icon/313528601/s100/tubitv.webp`,
	cts: `${JW_CONFIG.IMAGESCALER_URL}/icon/48600036/s100/curiositystream.webp`,
	aho: `${JW_CONFIG.IMAGESCALER_URL}/icon/343788557/s100/amazonhbomax.webp`,
};

/** SSR Safe way to get active provider promotions. */
export function useProviderPromotion(title?: MaybeRefOrGetter<TitleDetail>): {
	titleHasProviderPromotion: ComputedRef<boolean>;
	titleHasBackdropPromo: ComputedRef<boolean>;
	isPromotionOffer: (offer: TitleOfferFragment) => boolean;
	providerPromotion: Ref<PromoTextWithIcon | null>;
} {
	const titleDetailsStore = useTitleDetailsStore();
	const { setProviderPromotion } = titleDetailsStore;
	const { providerPromotion } = storeToRefs(titleDetailsStore);

	const { activeVariantWithControlGroup: primePlayPromoVariant } = useExperiment('EXP_PRIME_PLAY_PROMO');

	onServerPrefetch(() => setProviderPromotion(toValue(title)));

	// refetch on client (no hydration error) to get the updated status
	onMounted(() => setProviderPromotion(toValue(title)));

	const titleHasProviderPromotion = computed(() => providerPromotion.value != null);
	const titleHasBackdropPromo = computed(() => {
		if (!titleHasProviderPromotion.value || providerPromotion.value?.hideBackdropPromo) return false;
		if (providerPromotion.value?.experimentName === 'EXP_PRIME_PLAY_PROMO')
			return primePlayPromoVariant.value === 'variant_1';
		return true;
	});

	function isPromotionOffer(offer: TitleOfferFragment) {
		return (
			flatrateOrFreeOrAds.includes(offer.monetizationType) &&
			(providerPromotion.value as PromoTextWithIcon | null)?.provider === offer.package.shortName
		);
	}

	return {
		/** Is a provider promotion applicable to the current title - typically be play button (backdrop) promotion, or updating offer texts and ribbons. */
		titleHasProviderPromotion,

		/** Is a provider promotion applicable to the backdrop play button. */
		titleHasBackdropPromo,

		/** Check if the offer is under the current title's provider promotion. */
		isPromotionOffer,

		/** The applicable promotion itself. */
		providerPromotion,
	};
}

export function getPromoText(
	country: string,
	title: TitleDetail | null = null,
	currentDate: Date = new Date()
): PromoTextWithIcon | null {
	const promos = promotions[country as PromotionCountries];

	if (!promos || promos.length === 0 || title == null || title.offers.length === 0) return null;

	const promoText = promos.find(promo =>
		title.offers.some(
			offer => flatrateOrFreeOrAds.includes(offer.monetizationType) && offer.package.shortName === promo.provider
		)
	);

	if (promoText == null) return null;
	// Checking if we're within the promo dates
	if (promoText.startDate && +currentDate < new Date(promoText.startDate).setUTCHours(0, 0, 0, 0)) return null;
	if (promoText.endDate && +currentDate > new Date(promoText.endDate).setUTCHours(23, 59, 59, 999)) return null;

	return { ...promoText, icon: promotionProviderIcons[promoText.provider] };
}

// Disney External Pixel Tracking
const oceaniaLocales = new Set([WebLocale.au, WebLocale.nz]);
const noramLocales = new Set([WebLocale.ca]);
const emeaLocales = new Set([
	WebLocale.at,
	WebLocale.be,
	WebLocale.cz,
	WebLocale.dk,
	WebLocale.fi,
	WebLocale.fr,
	WebLocale.de,
	WebLocale.gr,
	WebLocale.hu,
	WebLocale.ie,
	WebLocale.it,
	WebLocale.lu,
	WebLocale.nl,
	WebLocale.no,
	WebLocale.pl,
	WebLocale.pt,
	WebLocale.ro,
	WebLocale.sk,
	WebLocale.es,
	WebLocale.se,
	WebLocale.ch,
	WebLocale.uk,
	WebLocale.tr,
]);
const latamLocales = new Set([
	WebLocale.br,
	WebLocale.mx,
	WebLocale.ar,
	WebLocale.cl,
	WebLocale.co,
	WebLocale.pe,
	WebLocale.uy,
	WebLocale.ec,
	WebLocale.pa,
	WebLocale.cr,
	WebLocale.py,
	WebLocale.bo,
	WebLocale.gt,
	WebLocale.ni,
	WebLocale.do,
	WebLocale.sv,
	WebLocale.hn,
	WebLocale.ve,
]);

export function getDisneyImpressionUrl(providerShortName: string | null | undefined, webLocale: WebLocale) {
	if (providerShortName === 'dnp' /* Disney+ */) {
		if (oceaniaLocales.has(webLocale) || noramLocales.has(webLocale))
			return 'https://imp.pxf.io/i/1206980/1833790/9358?sharedid=web';

		// move US to LATAM/EMEA pixel after they introduced VTR tracking. not clear yet how other markets will be handled in future
		if (latamLocales.has(webLocale) || emeaLocales.has(webLocale))
			return 'https://imp.pxf.io/i/1206980/1848147/9358?sharedid=web';

		if (webLocale === WebLocale.us) return 'https://imp.pxf.io/i/1206980/1809575/9358?sharedid=web_us';
	}

	if (providerShortName === 'srp' /* Star+ */) {
		if (latamLocales.has(webLocale)) return 'https://imp.pxf.io/i/1206980/1584316/13591?sharedid=web';
	}

	return null;
}
